import { AbsgArrayHelper, LogService } from '@absg/angular-common';
import { CalculationHelper, Constants, NimAlertService } from '@absg/nim-client-shared';
import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { BehaviorSubject, Observable } from 'rxjs';
import { finalize, take } from 'rxjs/operators';
import { WitnessConstants } from 'shared.root/constants/witness-constants';
import { ParentComponentTypes } from 'shared.root/enums/parentcomponent.enum';
import { WitnessItem } from 'shared.root/models/witness-base.model';
import { WitnessService } from 'shared.root/services/witness.service';
import { SubSink } from 'subsink';

@Component({
  selector: '[nc-witness-discrepancy]',
  templateUrl: './witness-discrepancy.component.html',
  standalone: false,
})
export class WitnessDiscrepancyComponent implements OnInit, OnDestroy {
  @Input() isWitnessDiscrepancyModalOpen: Observable<boolean>;
  @Output() saveWitnessDiscrepancyModal = new EventEmitter<WitnessItem[]>();
  @Input() inputDiscrepancyItems = new BehaviorSubject<WitnessItem[]>([]);
  @Input() selectedWitnessNDCs = new BehaviorSubject<number[]>([]);

  @ViewChild('txtDiscrepancyNotes', { static: false }) txtDiscrepancyNotes: ElementRef;
  displayWitnessDiscrepancyItems = new Array<WitnessItem>();
  private subsink = new SubSink();
  currentWitnessItem = new BehaviorSubject<WitnessItem>(null);
  isCalculatorModalOpen = new BehaviorSubject<boolean>(false);
  discrepancyNotes: UntypedFormControl;
  witnessForm: UntypedFormGroup;

  qohBeforeAfterText = WitnessConstants.qohBeforeDispenseText;
  saveButtonText = WitnessConstants.saveButtonText;
  discrepancyItems: WitnessItem[] = [];
  isSufficientQtyToDispense = true;
  isSpinnerVisible = false;
  witnessTransactionIds: Array<number> = [];
  modalOpenStatus = false;
  isCurrentItemNDCOutOfStock = false;
  patientOwnedDemographics = '';

  constructor(
    private formBuilder: UntypedFormBuilder,
    private nimAlertService: NimAlertService,
    private loggingService: LogService,
    private witnessService: WitnessService,
  ) { }

  ngOnInit(): void {
    this.createFormGroup();
    this.subsink.sink = this.isWitnessDiscrepancyModalOpen.subscribe(isOpen => {
      if (isOpen) {
        this.modalOpenStatus = isOpen;
        this.discrepancyItems = this.inputDiscrepancyItems.getValue();
        if (!AbsgArrayHelper.isNil(this.discrepancyItems)) {

          this.resetForm();
          this.mapInputForDisplay();
          this.moveToNextPosition();
          this.getDynamicMessageSecondLine();
        }
      } else {
        this.modalOpenStatus = false;
      }
    });
  }

  ngOnDestroy(): void {
    this.subsink.unsubscribe();
  }

  createFormGroup(): void {
    this.witnessForm = this.formBuilder.group({
      discrepancyNotes: ['discrepancyNotes'],
    });
    this.discrepancyNotes = this.witnessForm.controls['discrepancyNotes'] as UntypedFormControl;
    this.resetForm();
  }

  resetForm(): void {
    this.discrepancyNotes.setValue('');
    this.setFocus();
  }

  saveAndContinueDiscrepancy(): void {
    this.updateReturnWitnessItemNotes(false);
    this.updateWitness();
  }

  saveAndSkipDiscrepancy(): void {
    this.updateReturnWitnessItemNotes(true);
    this.updateWitness();
  }

  onKeyDown(e: KeyboardEvent): void {
    // no white space as the first character
    // keyCode values belong to whitespace characters
    if (!this.txtDiscrepancyNotes.nativeElement.value) {
      if (e.keyCode === 32) {
        e.preventDefault();
      }
    }

  }

  onKeyUp(): void {
    this.txtDiscrepancyNotes.nativeElement.value = this.ltrim(this.txtDiscrepancyNotes.nativeElement.value.toString());
  }

  ltrim(str): string {
    return str.replace(/^\s+/g, '');
  }

  cycleToNextItemOrCloseModal(): void {
    // this will go to the next position in the positions array and will close only if no more items are present in the array
    if (AbsgArrayHelper.isNil(this.displayWitnessDiscrepancyItems) || this.displayWitnessDiscrepancyItems.length === 0) {
      this.witnessService.witnessTransactionIds.next(this.witnessTransactionIds);
      this.saveWitnessDiscrepancyModal.emit(this.discrepancyItems);
      this.witnessTransactionIds = [];
    } else {
      this.moveToNextPosition();
    }
    this.resetForm();
    this.getDynamicMessageSecondLine();
  }

  getQOHEntered(): number {
    const item = this.currentWitnessItem.getValue();
    if (!AbsgArrayHelper.isNil(item)) {
      return CalculationHelper.add(item.userInputQtyInvUnitInCabinet,
        item.userInputQtyInvUnitInUTH ? (item.userInputQtyInvUnitInUTH) : 0);
    }
    return 0;
  }

  getQOH(): number {
    const item = this.currentWitnessItem.getValue();
    if (!AbsgArrayHelper.isNil(item)) {
      return CalculationHelper.convertToQuantity(
        CalculationHelper.add(item.qtyOnHandInUTH, item.qtyOnHandInCabinet),
        item.dispenseUnitOfMeasureConversionFactor, item.inventoryUnitOfMeasureConversionFactor);
    }
    return 0;
  }

  getQohChangedText(): string {
    const item = this.currentWitnessItem.getValue();
    const uniqSpiIds = AbsgArrayHelper.unionBy(this.discrepancyItems, (ele) => ele.supplierProductInclusionId)
      .map(elem => elem.supplierProductInclusionId);
    if (!AbsgArrayHelper.isNil(item)) {
      if (this.getQOHEntered() === this.getQOH()) {
        if (item.isBinned() && uniqSpiIds.length > 1) {
          return 'Total QOH same but changed across NDCs.';
        } else {
          return 'Total QOH same but changed across lot numbers.';
        }
      }
    }
    return '';
  }

  moveToNextPosition(): void {
    if (!Array.isArray(this.displayWitnessDiscrepancyItems)) {
      return;
    }
    this.currentWitnessItem.next(this.displayWitnessDiscrepancyItems.shift());
    const currentItem = this.currentWitnessItem.getValue();
    if (!AbsgArrayHelper.isNil(currentItem)) {
      this.isCurrentItemNDCOutOfStock = currentItem.parentComponent === ParentComponentTypes.TransferOut &&
        this.isSelectedItemNDCOutOfStock() && currentItem.isBinned();
      this.patientOwnedDemographics = currentItem.getPatientOwnedDemographics();
    }
  }

  setDefaultFocus(event: TransitionEvent = null): void {
    if (!this.modalOpenStatus) {
      return; // exit if modal is closed
    }
    if (!event || (event && event.propertyName === Constants.transitionEventPropertyName)) {
      this.setFocus();
    }
  }

  getDynamicMessageFirstLine(): string {
    let dynamicMessageFirstLine = '';
    const witnessItem = this.currentWitnessItem.getValue();
    if (!AbsgArrayHelper.isNil(witnessItem)) {
      const userInputDispenseUnit = CalculationHelper.convertToQuantity(CalculationHelper.add(witnessItem.userInputQtyInvUnitInCabinet,
        witnessItem.userInputQtyInvUnitInUTH ? witnessItem.userInputQtyInvUnitInUTH : 0),
        witnessItem.inventoryUnitOfMeasureConversionFactor,
        witnessItem.dispenseUnitOfMeasureConversionFactor);

      dynamicMessageFirstLine = this.getCombinedDynamicMessage(witnessItem.parentComponent, userInputDispenseUnit,
        witnessItem.reqDispenseUnitAdministerQuantity, true, witnessItem.skipTransaction);
    }
    return dynamicMessageFirstLine;
  }

  isButtonsEnabled(): boolean {
    let isValid = false;
    if (!AbsgArrayHelper.isNil(this.discrepancyNotes.value) && this.discrepancyNotes.value.trim() !== '') {
      isValid = true;
    }
    return isValid && this.witnessForm.valid;
  }

  getDynamicMessageSecondLine(): string {
    let dynamicMessageSecondLine = '';
    const witnessItem = this.currentWitnessItem.getValue();

    if (!AbsgArrayHelper.isNil(witnessItem)) {
      const userInputDispenseUnit = CalculationHelper.convertToQuantity(CalculationHelper.add(witnessItem.userInputQtyInvUnitInCabinet,
        witnessItem.userInputQtyInvUnitInUTH ? witnessItem.userInputQtyInvUnitInUTH : 0),
        witnessItem.inventoryUnitOfMeasureConversionFactor,
        witnessItem.dispenseUnitOfMeasureConversionFactor);

      dynamicMessageSecondLine = this.getCombinedDynamicMessage(witnessItem.parentComponent, userInputDispenseUnit,
        witnessItem.reqDispenseUnitAdministerQuantity, false, witnessItem.skipTransaction);

      if (witnessItem.parentComponent === ParentComponentTypes.DispenseSummary) {
        this.handleDynamicContentForDispenseSummary();
        dynamicMessageSecondLine = '';
      } else if (witnessItem.parentComponent === ParentComponentTypes.Restock) {
        this.qohBeforeAfterText = WitnessConstants.qohBeforeRestockText;
      } else if (witnessItem.parentComponent === ParentComponentTypes.Move) {
        this.qohBeforeAfterText = WitnessConstants.qohBeforeMoveText;
      } else if (witnessItem.parentComponent === ParentComponentTypes.Return) {
        this.qohBeforeAfterText = WitnessConstants.qohBeforeReturnText;
      } else if (witnessItem.parentComponent === ParentComponentTypes.Unload) {
        this.handleDynamicContentForUnload();
      } else if (witnessItem.parentComponent === ParentComponentTypes.PurchaseOrderDetail) {
        witnessItem.isInSufficientQtyReq = false;
        this.isSufficientQtyToDispense = true;
        this.qohBeforeAfterText = WitnessConstants.qohBeforePurchaseOrderText;
      } else if (witnessItem.parentComponent === ParentComponentTypes.TransferOut) {
        this.qohBeforeAfterText = WitnessConstants.qohBeforeTransferOut;
      } else if (witnessItem.parentComponent === ParentComponentTypes.CancelTransfer) {
        this.qohBeforeAfterText = WitnessConstants.qohBeforeCancelTransfer;
      } else if (witnessItem.parentComponent === ParentComponentTypes.TransferIn) {
        this.qohBeforeAfterText = WitnessConstants.qohBeforeTransferIn;
      } else {
        if (userInputDispenseUnit === 0) {
          witnessItem.isInSufficientQtyReq = true;
          this.isSufficientQtyToDispense = false;
        } else {
          if (witnessItem.reqDispenseUnitAdministerQuantity > userInputDispenseUnit) {
            witnessItem.isInSufficientQtyReq = true;
            this.isSufficientQtyToDispense = false;
          } else {
            this.isSufficientQtyToDispense = true;
          }
        }
      }
    }
    return dynamicMessageSecondLine;
  }

  isSufficientQty(): boolean {
    return this.isSufficientQtyToDispense;
  }

  handleDynamicContentForDispenseSummary(): void {
    this.saveButtonText = 'Save';
    this.qohBeforeAfterText = 'QOH After Dispense';
  }

  handleDynamicContentForUnload(): void {
    const item = this.currentWitnessItem.getValue();
    this.saveButtonText = item.isBinned() ? WitnessConstants.saveButtonText : 'Save';
    this.qohBeforeAfterText = 'QOH Before Unload';
  }

  getCurrentItem(): WitnessItem {
    return this.currentWitnessItem.getValue();
  }

  private isSelectedItemNDCOutOfStock(): boolean {
    const item = this.currentWitnessItem.getValue();
    if (!AbsgArrayHelper.isNil(item)) {
      item.isInSufficientQtyReq = (item.parentComponent === ParentComponentTypes.TransferOut)
        && this.checkSelectedNDCsOutOfStock(item);
      item.skipTransaction = item.isInSufficientQtyReq;
      return item.isInSufficientQtyReq;
    }
    return false;
  }

  private checkSelectedNDCsOutOfStock(currentItem: WitnessItem): boolean {
    const ndcItems = this.selectedWitnessNDCs.getValue();
    let totalSelectedNDCQoh = 0;
    if (ndcItems.length > 0) {
      const filteredDispcrepancyItems = AbsgArrayHelper.filter(this.discrepancyItems, y => y.positionContainerId === currentItem.positionContainerId);
      AbsgArrayHelper.forEach(ndcItems, item => {
        const filteredItems = AbsgArrayHelper.filter(filteredDispcrepancyItems, x => x.supplierProductInclusionId === item);
        totalSelectedNDCQoh = totalSelectedNDCQoh + AbsgArrayHelper.reduce(filteredItems, (sum, current) => sum + current.userInputQtyInvUnitInCabinet, 0);
      });
    }
    return totalSelectedNDCQoh === 0;
  }

  private getCombinedDynamicMessage(parentComponent: string, userInputDispenseUnit: number,
    administratorQuantity: number, firstLine?: boolean, skipTransaction?: boolean): string {
    let returnString = '';

    if (firstLine) {
      if (userInputDispenseUnit === 0) {
        return returnString;
      }
      if (parentComponent !== ParentComponentTypes.Dispense && administratorQuantity > userInputDispenseUnit &&
        !skipTransaction) {
        returnString = 'Do you wish to Continue?';
      }
    } else {
      if (parentComponent === ParentComponentTypes.ClinicCharge) {
        this.qohBeforeAfterText = WitnessConstants.qohBeforeChargeText;
      }
      this.saveButtonText = WitnessConstants.saveButtonText;

      if (userInputDispenseUnit === 0) {
        if (parentComponent === ParentComponentTypes.ClinicCharge) {
          returnString = WitnessConstants.clinicChargeOutOfStockDynamicMessage;
        } else if (parentComponent === ParentComponentTypes.TransferOut) {
          returnString = WitnessConstants.transferOutItemOutOfStockDynamicMessage;
        } else {
          returnString = WitnessConstants.dispenseOutOfStockDynamicMessage;
        }
      } else if (skipTransaction) {
        if (parentComponent === ParentComponentTypes.ClinicCharge) {
          returnString = WitnessConstants.clinicChargeLessQuantityDynamicMessage;
        } else if (parentComponent === ParentComponentTypes.TransferOut) {
          returnString = WitnessConstants.transferOutItemOutOfStockDynamicMessage;
        } else {
          returnString = WitnessConstants.dispenseLessQuantityDynamicMessage;
        }
      }
    }
    return returnString;
  }

  private updateWitness(): void {
    if (!AbsgArrayHelper.isNil(this.discrepancyItems) && this.discrepancyItems.length > 0) {
      this.isSpinnerVisible = true;
      const currentItem = this.currentWitnessItem.getValue();
      let items: Array<WitnessItem> = [];
      if (currentItem.isBinItem) {
        // get all the bin items
        items = this.discrepancyItems
          .filter(item => item.positionContainerId === currentItem.positionContainerId && item.accessPointId === currentItem.accessPointId);
      } else {
        items = this.discrepancyItems.filter(item =>
          item.supplierProductInclusionId === currentItem.supplierProductInclusionId);
      }

      if (!AbsgArrayHelper.isNull(items) && items.length > 0 && currentItem.isBinItem && !currentItem.isPracticeStatus()) {
        const containerId = currentItem.positionContainerId;
        this.witnessService.getAutoReorderItemIdForTransactionRequest(containerId, items[0].productId, items)
          .pipe(take(1))
          .subscribe(autoReorderProductDetail => {
            AbsgArrayHelper.each(items, item => {
              item.autoReorderspInclusionId = autoReorderProductDetail.supplierProductInclusionId;
              item.autoReorderspItemId = autoReorderProductDetail.itemId;
              item.autoReorderTradeName = autoReorderProductDetail.tradeName;
            });
            this.saveWitnessItems(items);
          });
      } else {
        this.saveWitnessItems(items);
      }
    }
  }

  private saveWitnessItems(witnessItems: WitnessItem[]): void {
    this.isSpinnerVisible = true;

    this.witnessService.witnessInventory(witnessItems)
      .pipe(take(1),
        finalize(() => {
          this.isSpinnerVisible = false;
        }))
      .subscribe((transaction) => {
        if (transaction !== null) {
          this.witnessTransactionIds.push(transaction.transactionId);
          this.cycleToNextItemOrCloseModal();
          this.witnessForm.reset();
        } else {
          this.cycleToNextItemOrCloseModal();
          this.nimAlertService.showErrorAlert('Error in witness transaction', new Error('Transaction did not save'));
        }
      },
        (error: Error) => {
          this.isSpinnerVisible = false;
          this.cycleToNextItemOrCloseModal();
          this.loggingService.logError(error);
          this.nimAlertService.showErrorAlert('Error in witness transaction', error);
        });
  }

  private mapInputForDisplay(): void {
    this.displayWitnessDiscrepancyItems = [];
    this.discrepancyItems.forEach(item => {
      const filterItem = this.displayWitnessDiscrepancyItems
        .find(displayItem => displayItem.accessPointId === item.accessPointId
          && displayItem.positionContainerId === item.positionContainerId);
      if (AbsgArrayHelper.isNil(filterItem)) {
        this.displayWitnessDiscrepancyItems.push(AbsgArrayHelper.cloneDeep(item));
      } else {
        filterItem.qtyOnHandInCabinet += item.qtyOnHandInCabinet;
        filterItem.qtyOnHandInUTH += item.qtyOnHandInUTH;
        filterItem.userInputQtyInvUnitInCabinet += item.userInputQtyInvUnitInCabinet;
        filterItem.userInputQtyInvUnitInUTH += (item.userInputQtyInvUnitInUTH ? item.userInputQtyInvUnitInUTH : 0);
        filterItem.reqDispenseUnitAdministerQuantity += item.reqDispenseUnitAdministerQuantity;
      }
    });
  }

  private setFocus(): void {
    this.txtDiscrepancyNotes?.nativeElement.focus();
  }

  private updateReturnWitnessItemNotes(skipFlag: boolean): void {
    if (!AbsgArrayHelper.isNil(this.discrepancyItems) && this.discrepancyItems.length > 0) {
      const currentItem = this.currentWitnessItem.getValue();
      // bin flow
      if (currentItem.isBinItem) {
        this.discrepancyItems.filter(item =>
          item.accessPointId === currentItem.accessPointId && item.positionContainerId === currentItem.positionContainerId)
          .forEach(item => {
            item.notes = this.discrepancyNotes.value;
            item.skipTransaction = skipFlag;
          });
      } else {
        // normal flow
        this.discrepancyItems.filter(item =>
          item.supplierProductInclusionId === currentItem.supplierProductInclusionId,
        ).forEach(item => {
          item.notes = this.discrepancyNotes.value;
          item.skipTransaction = skipFlag;
        });
      }
    }
  }
}
